/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { AxiosError, isAxiosError } from 'axios';
import { ImpoundPolicyListResponse, SubmitImpoundResponse } from './queryFunctions';
import axiosCall from '../services/axios';
import { getUserStorage } from '../storage/userStorage';

const months: Array<string> = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

export function round(value: number, decimals: number) {
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: false,
  });
  return parseFloat(formatter.format(value));
}

export const handleAxiosError = (err: AxiosError): string => {
  if (!isAxiosError(err)) return '';
  if (err.response) {
    return getErrorFromEndpoint(err.response.data);
  } else if (err.request) {
    return err.request;
  } else {
    return err.message;
  }
};

const getErrorsFromRes = (res: any, existingErr: string) => {
  if ('error' in res) {
    existingErr += isStrEmptyOrNull(res.error) ? '' : res.error;
  }
  if ('errorMessage' in res) {
    existingErr += isStrEmptyOrNull(res.errorMessage) ? '' : ' ' + res.errorMessage;
  }
  if ('errors' in res) {
    existingErr += isStrEmptyOrNull(res.errors) ? '' : res.errors;
  }
  return existingErr;
};

export const getErrorFromEndpoint = (res: any | AxiosError, batchendpoint: string = '') => {
  if (isAxiosError(res)) return handleAxiosError(res);
  if (res instanceof Error) {
    return res.message;
  }
  if (isObjEmptyOrNull(res)) {
    return '';
  } else {
    let err = '';
    err = getErrorsFromRes(res, err);
    if (!isStrEmptyOrNull(batchendpoint) && batchendpoint !== 'error' && batchendpoint !== 'errorMessage') {
      if (batchendpoint in res) {
        res = res[batchendpoint];
        err = getErrorsFromRes(res, err);
      }
    }
    return err;
  }
};

export function getMatchingLoanTypes(investorLoanType: string) {
  var loanTypeSet = new Set();
  var len = investorLoanType.length || 0;
  var wildcardChar = '0';
  for (var i = 0; i < len; i++) {
    const repeatedString = wildcardChar.repeat(len - (i + 1));
    loanTypeSet.add(investorLoanType.substring(0, i + 1) + repeatedString);
  }
  return loanTypeSet;
}

//Checks if a string is not null and not just whitespace
export const isStrEmptyOrNull = (value: string) => {
  if (typeof value !== 'string') {
    // check if the string variable is some type other than string
    return true;
  }
  if (!value) {
    return true;
  } else {
    return !(value.trim().length > 0);
  }
};

//Checks if a string is alphanumeric
export const isAlphanumeric = (value: string) => {
  if (typeof value !== 'string') {
    // check if the string variable is some type other than string
    return false;
  }
  if (!value) {
    return false;
  } else {
    return /^[a-zA-Z0-9]+$/.test(value);
  }
};

export const isDateEmptyOrNull = (value: number) => {
  if (typeof value === 'number' && value !== null && value !== undefined) {
    return false;
  }
  return true;
};

export const isObjEmptyOrNull = (obj: object) => {
  if (typeof obj === 'object' && obj !== null && obj !== undefined) {
    if (Array.isArray(obj)) {
      return obj.length === 0;
    }
    if (obj instanceof Blob) {
      return false;
    }
    return !Object.values(obj).some((v) => v !== null && typeof v !== 'undefined');
  } else {
    return true;
  }
};

/*
  Validates the routing number is 9 consecutive digits and fits the following algorithm:
  The first, fourth and seventh digits from the left are mulitplied by 3
  The second, fifth, and eigth digits are multiplied by 7
  All of those are added with the remaining third, sixth and ninth digits
  If the total is divisible by 10, the routing number should be valid.
*/
export const isValidRoutingNumber = (value: string) => {
  if (isStrEmptyOrNull(value)) {
    return false;
  }
  const routingNoRegex = /^[0-9]{9}$/;
  if (!value.match(routingNoRegex)) {
    return false;
  }
  const prefix = value.substring(0, 2);
  const prefixRegex = /^(0[1-9]|1[0-2]|2[1-9]|3[0-2]|6[1-9]|7[0-2]|80)$/;
  if (!prefix.match(prefixRegex)) {
    return false;
  }
  let digitArray = value.split('');
  let total = 0;
  for (let i = 0; i < 9; i++) {
    let digit = parseInt(digitArray[i]);
    switch (i) {
      case 0:
      case 3:
      case 6:
        total += digit * 3;
        break;
      case 1:
      case 4:
      case 7:
        total += digit * 7;
        break;
      default:
        total += digit;
        break;
    }
  }

  return total % 10 === 0;
};

export const isValidLoanNo = (value: string) => {
  if (isStrEmptyOrNull(value)) {
    return false;
  }

  const loanNoRegex = /^(\d{12})$/;
  return value.match(loanNoRegex);
};

export const isValidEmailAddress = (value: string) => {
  if (isStrEmptyOrNull(value)) {
    return false;
  }
  const emailAddressRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return value.match(emailAddressRegex);
};

// Checks if a string has at least 8 characters, one uppercase, one lowercase, one number, one special character
export const isValidPassword = (value: string) => {
  if (isStrEmptyOrNull(value)) {
    return false;
  }

  const passwordRegex =
    /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~])[0-9a-zA-Z!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]{8,}/;
  return value.match(passwordRegex);
};

// Checks if a re-enter field is not empty and matches the original field
export const isValidReenterField = (field: string, reenterField: string) => {
  return !isStrEmptyOrNull(reenterField) && reenterField === field;
};

export const isObjEmpty = (
  obj: ImpoundPolicyListResponse[] | Record<string, string> | SubmitImpoundResponse,
  name: string,
): boolean => {
  if (obj === undefined || obj === null) {
    return true;
  }
  if (Array.isArray(obj)) {
    return obj.length === 0;
  }
  return Object.keys(obj).length === 0;
};

export const isStrEmpty = (str: string): boolean => {
  if (str === undefined || str === null) return true;
  const trimmedString: string = str.toLowerCase().trim();
  if (trimmedString !== '') {
    return false;
  } else {
    return true;
  }
};

export const hyphenateCompCode = (compCode: string): string => {
  if (typeof compCode !== 'string' || compCode.length !== 9) {
    return compCode;
  }
  let hyphenIndex: number = compCode.length - 3;
  let prefix: string = compCode.slice(0, hyphenIndex);
  let suffix: string = compCode.slice(hyphenIndex);
  return prefix + '-' + suffix;
};

export const phoneNumberFormatter = (number: string, ext: string): string => {
  if (!number || typeof number !== 'string' || number.length !== 10) {
    return '';
  }

  let formattedNumber: string = `${number.substring(0, 3)}-${number.substring(3, 6)}-${number.substring(6, 10)}`;
  if (ext && typeof ext === 'string') formattedNumber = formattedNumber + `, Ext. ${ext}`;
  return formattedNumber;
};

export const getUtcOffset = (): string => {
  const currentDate: Date = new Date();
  const timeZoneOffset: number = currentDate.getTimezoneOffset();
  const hoursOffset: number = Math.abs(Math.floor(timeZoneOffset / 60));
  const minutesOffset: number = Math.abs(timeZoneOffset % 60);
  const offsetSign: '+' | '-' = timeZoneOffset < 0 ? '+' : '-';
  const formattedOffset: string = `${offsetSign}${hoursOffset.toString().padStart(2, '0')}:${minutesOffset
    .toString()
    .padStart(2, '0')}`;
  console.log(formattedOffset);
  return formattedOffset;
};

export const epochToDate = (epoch: number): Date => {
  if (!epoch || typeof epoch !== 'number') {
    throw new Error('epochToDate() -> Invalid epoch value given');
  }
  const date: Date = new Date(epoch * 1000);
  return date;
};

export const epochToPST8AMDate = (epoch: number): Date => {
  if (!epoch || typeof epoch !== 'number') {
    throw new Error('epochToPSTDate() -> Invalid epoch value given');
  }
  const epochAsDate = new Date(`${epoch}`.length === 10 ? epoch * 1000 : epoch);
  const month = new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    timeZone: 'America/Los_Angeles',
  }).format(epochAsDate);
  const day = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    timeZone: 'America/Los_Angeles',
  }).format(epochAsDate);
  const year = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    timeZone: 'America/Los_Angeles',
  }).format(epochAsDate);
  const pstDate = new Date();
  pstDate.setMonth(parseInt(month) - 1);
  pstDate.setDate(parseInt(day));
  pstDate.setFullYear(parseInt(year));
  pstDate.setHours(8, 0, 0, 0);
  return pstDate;
};

export const getEpochAsYYYYMMDD = (epoch: number): string => {
  const epochAsDate = new Date(`${epoch}`.length === 10 ? epoch * 1000 : epoch);
  const month = new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    timeZone: 'America/Los_Angeles',
  }).format(epochAsDate);
  const day = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    timeZone: 'America/Los_Angeles',
  }).format(epochAsDate);
  const year = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    timeZone: 'America/Los_Angeles',
  }).format(epochAsDate);
  return year + month + day;
};

export const dateToEpoch = (date: Date): number => {
  if (!(date instanceof Date)) {
    throw new Error('dateToEpoch() -> Input must be a Date object');
  }
  if (isNaN(date.getTime())) {
    throw new Error('dateToEpoch() -> Invalid Date object');
  }
  const epochMilliseconds: number = date.getTime();
  const epochSeconds: number = Math.floor(epochMilliseconds / 1000);
  return epochSeconds;
};

// MM/DD/YYYY format by default
export const formatDate = (date: Date, separator: string = '/'): string => {
  if (!(date instanceof Date)) {
    return '';
  }
  if (isNaN(date.getTime())) {
    return '';
  }
  const month: string = String(date.getMonth() + 1).padStart(2, '0'); //need to add one to the javascript index
  const day: string = String(date.getDate()).padStart(2, '0');
  const year: number = date.getFullYear();
  const formattedDate: string = `${month}${separator}${day}${separator}${year}`;
  return formattedDate;
};

// YYYY-MM-DD format by default
export const formatDateYYYYMMDD = (date: Date, separator: string = '-'): string => {
  if (!(date instanceof Date)) {
    return '';
  }
  if (isNaN(date.getTime())) {
    return '';
  }
  const month: string = String(date.getMonth() + 1).padStart(2, '0'); //need to add one to the javascript index
  const day: string = String(date.getDate()).padStart(2, '0');
  const year: number = date.getFullYear();
  const formattedDate: string = `${year}${separator}${month}${separator}${day}`;
  return formattedDate;
};

//Does approximately the same as epochToAnniDate, but may avoid complicaitons regarding time zone differences based on usage purpose
export const getTimestampAsDate = (timestamp?: number | string | null): string => {
  if (!timestamp || isNaN(Number(timestamp))) return '';

  const date = new Date(Number(timestamp) * 1000);
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

/**
 * Convert an ISO “YYYY-MM-DD” string into “MM/DD/YYYY”.
 */
export const formatIsoDate = (iso?: string): string => {
  if (!iso) return '';
  const parts = iso.split('-');
  if (parts.length !== 3) return '';
  const [year, month, day] = parts;
  if (!/^\d{4}$/.test(year) || !/^\d{2}$/.test(month) || !/^\d{2}$/.test(day)) {
    return '';
  }
  return `${month}/${day}/${year}`;
};

// Helper function to adjust the date value for input[type="date"]
export const formatDateForInputWithOffset = (timestamp) => {
  const date = new Date(timestamp * 1000);
  const year = date.getFullYear().toString().padStart(4, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const getLpiDueDateString = (lpi?: number): string | null => {
  return getEpochAsYYYYMMDD(addMonthsYrsToTimestamp(lpi, 1, 0));
};

export const firstOfMonth = (() => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}${m}01`;
})();

export const formatDollars = (amountStr: string): string => {
  try {
    const amount = Number.parseFloat(amountStr);
    const formattedNum = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
    return formattedNum;
  } catch (e) {
    console.error(e);
    return amountStr;
  }
};

export const calculateServicingLTV = (upb: number, marketValue: number, purchasePrice: number): number => {
  const validValues = [marketValue, purchasePrice].filter((v) => v > 0);

  if (validValues.length === 0) {
    console.warn('Both marketValue and purchasePrice are 0. Cannot calculate LTV.');
    return 0;
  }

  const lowerValue = Math.min(...validValues);
  const rawLtv = (upb / lowerValue) * 100;

  const roundedLtv = Math.round(rawLtv * 100) / 100;

  return roundedLtv;
};

export const calculateDaysPastDue = (lpi: number, servicingState: string): number => {
  if (servicingState !== '1') return 0;
  const paidThroughDate = new Date(lpi * 1000);

  const dueDate = new Date(paidThroughDate);
  dueDate.setMonth(dueDate.getMonth() + 1);

  const today = new Date();
  dueDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const diffInMs = today.getTime() - dueDate.getTime();
  const daysPastDue = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  return Math.max(0, daysPastDue);
};

export const lpiBeforePreviousMonth = (lpi: number): boolean => {
  if (!lpi) {
    return false;
  }
  const paidThroughDate = new Date(lpi * 1000);

  const previousMonth = new Date();
  previousMonth.setMonth(previousMonth.getMonth() - 1, 1); //first day of previous month
  paidThroughDate.setHours(0, 0, 0, 0);
  previousMonth.setHours(0, 0, 0, 0);

  //return if paid throught date is after the start of previous month
  return paidThroughDate.getTime() < previousMonth.getTime();
};

export const formatSSN = (ssn: string | undefined) => {
  if (!ssn) return '';
  const cleaned = ssn.replace(/\D/g, '');
  if (cleaned.length === 9) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5, 9)}`;
  }
  return cleaned;
};

export const epochToAnniDate = (epoch: number): string => {
  if (!epoch || typeof epoch !== 'number') {
    return 'No valid epoch';
  }
  const date: Date = new Date(epoch * 1000);

  date.setFullYear(date.getFullYear() + 1);
  const year: number = date.getFullYear();
  const isLeapYear: boolean = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

  date.setDate(0);
  let month: number = date.getMonth();
  if (month === 1 && isLeapYear) date.setDate(29);

  const day: number = date.getDate();
  month = month + 1; ////need to add one to the javascript index
  const formattedDate: string = `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
  return formattedDate;
};

export const epochToAnniMonth = (epoch: number) => {
  if (!epoch || typeof epoch !== 'number') {
    return 'No valid epoch';
  }

  const date: Date = new Date(epoch * 1000);
  date.setDate(0);
  const monthIndex: number = date.getMonth();
  //capatalize first letter of the month
  return months[monthIndex].charAt(0).toUpperCase() + months[monthIndex].slice(1);
};

export const addMonthsToDate = (inputDate: number, numMonths: number): string => {
  if (!inputDate || typeof inputDate !== 'number') {
    return 'Invalid input date';
  }
  if (!numMonths || typeof numMonths !== 'number') {
    return 'Invalid num of months';
  }

  const date =
    typeof inputDate === 'string'
      ? new Date(inputDate)
      : new Date(`${inputDate}`.length === 10 ? inputDate * 1000 : inputDate);
  let month: number = date.getMonth();
  let year: number = date.getFullYear();

  month += numMonths;
  year += Math.floor(month / 12);
  month = month % 12;

  date.setMonth(month);
  date.setFullYear(year);

  const day: number = date.getDate();
  month = date.getMonth() + 1; //need to add one to the javascript index
  year = date.getFullYear();

  const formattedDate: string = `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
  return formattedDate;
};

export const addMonthsYrsToTimestamp = (inputDate: number, numMonths: number, numYrs: number): number => {
  if (!inputDate || typeof inputDate !== 'number') {
    return -1;
  }
  if (typeof numMonths !== 'number') {
    return -1;
  }
  if (typeof numYrs !== 'number') {
    return -1;
  }

  const date = epochToPST8AMDate(inputDate);
  const newDate = new Date(date);

  newDate.setMonth(newDate.getMonth() + numMonths);
  newDate.setFullYear(newDate.getFullYear() + numYrs);

  return newDate.getTime() / 1000;
};
function dateOnly(date1: Date) {
  date1.setHours(0);
  date1.setMinutes(0);
  date1.setSeconds(0);
  date1.setMilliseconds(0);
}
export function daysBetween(date1: Date, date2: Date) {
  let oneDay = 24 * 60 * 60 * 1000;
  let day1 = date1;
  let day2 = date2;
  // remove any time from the dates.
  dateOnly(day1);
  dateOnly(day2);
  let days = round((day2.getTime() - day1.getTime()) / oneDay, 0);
  return days;
}

export function removeEmpty(obj: object) {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null && v.length !== 0 && v !== undefined));
}

export function formatYearMonthDay(year, month, day) {
  //var formatteddate = ("000" + (year)).slice(-4) + "-" + ("0" + (month)).slice(-2) + "-" + ("0" + (day)).slice(-2);
  var formatteddate = year + '-' + month + '-' + day;
  return formatteddate;
}

export function formatAsDatabaseDate(year: number | string, month: number | string, day: number | string) {
  var formatteddate = ('000' + year).slice(-4) + ('0' + month).slice(-2) + ('0' + day).slice(-2);
  return formatteddate;
}

// input: yyyymmdd
// output: Mon dd, yyyy
export function formatAbbrevDateStr(dateStr: string) {
  if (dateStr.length !== 8) return null;
  const year = +dateStr.substring(0, 4);
  const month = +dateStr.substring(4, 6) - 1;
  const day = +dateStr.substring(6, 8);
  const date = new Date(year, month, day);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// input: MM/dd/yyyy
// output: Mon dd, yyyy
export function formatAbbrevDateMMddyyyy(dateStr: string) {
  const datesArr = dateStr.split('/');
  const year = +datesArr[2];
  const month = +datesArr[0] - 1;
  const day = +datesArr[1];

  const date = new Date(year, month, day);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function isTodayWithinFirst5BusinessDaysOfMonth() {
  const dte = new Date();
  if (dte.getDay() <= 5 && dte.getDay() > 0 && dte.getDate() <= 7) return true;
  return false;
}

export function getDefaultReportingDate() {
  let dte = new Date();
  if (isTodayWithinFirst5BusinessDaysOfMonth()) {
    dte.setMonth(dte.getMonth() - 1);
    dte.setDate(1);
  } else {
    dte.setDate(1);
  }
  return dte;
}

export function formatMMDDYYYY(date: string) {
  //yyyy-mm-dd to mm/dd/yyyy
  var dateArr = date.split('-');
  return dateArr[1] + '/' + dateArr[2] + '/' + dateArr[0];
}
export function convertDateInputTo8AMUTC(dateToConvert) {
  //yyyy-mm-dd
  var dateformatted = dateToConvert.split('-');
  var UTC_8AM_Date = new Date(Date.UTC(dateformatted[0], dateformatted[1] - 1, dateformatted[2], 8));
  return UTC_8AM_Date;
}

export function convertDateInputTo8AMLocal(dateToConvert) {
  //yyyy-mm-dd
  const dateformatted = dateToConvert.split('-');
  var newDate = new Date();
  newDate.setFullYear(dateformatted[0]);
  newDate.setDate(Number(dateformatted[2]));
  newDate.setMonth(dateformatted[1] - 1);
  newDate.setHours(8, 0, 0, 0);
  return newDate;
}

export function getCurrentDate8AMUTC() {
  const currdate = new Date();
  var UTC_8AM_currdate = new Date(Date.UTC(currdate.getFullYear(), currdate.getMonth(), currdate.getDate(), 8));
  return UTC_8AM_currdate;
}

export function convertDateToUTCString(datetimestamp) {
  var intlDateObj = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  var usaTime = intlDateObj.format(datetimestamp);
  return usaTime;
}

export const convertToPST = (timestamp) => {
  const date =
    typeof timestamp === 'string'
      ? new Date(timestamp)
      : new Date(`${timestamp}`.length === 10 ? timestamp * 1000 : timestamp);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString('en-US', {
    hour12: true,
    timeZone: 'America/Los_Angeles',
  });
};

// Convert QID BRW1_CD.E05 TimeZone Codes to IANA TimeZone Identifiers
export const getTimeZoneIdentfierFromCode = (timeZone) => {
  let identifier = '';
  switch (timeZone) {
    case 'AHA':
      identifier = 'America/Halifax';
      break;
    case 'APR':
      identifier = 'America/Puerto_Rico';
      break;
    case 'CTR':
      identifier = 'US/Central';
      break;
    case 'EST':
      identifier = 'US/Eastern';
      break;
    case 'MTN':
      identifier = 'US/Mountain';
      break;
    case 'PAC':
      identifier = 'US/Pacific';
      break;
    case 'PGU':
      identifier = 'Pacific/Guam';
      break;
    case 'UAK':
      identifier = 'US/Alaska';
      break;
    case 'UAZ':
      identifier = 'US/Arizona';
      break;
    case 'UHI':
      identifier = 'US/Hawaii';
      break;
    default:
      break;
  }
  return identifier;
};

// Get the time in a given time zone
export const getCurrentTimeInTimeZone = (timeZone, isCode) => {
  const date = new Date();

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return '';
  }

  try {
    const timeZoneIdentifier = isCode ? getTimeZoneIdentfierFromCode(timeZone) : timeZone;

    return date.toLocaleTimeString('en-US', {
      hour12: true,
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timeZoneIdentifier,
    });
  } catch (e) {
    console.error(e);
    return '';
  }
};

// get the hour in a given time zone
export const getCurrentHourInTimeZone = (timeZone, isCode) => {
  if (!timeZone) {
    return -1;
  }

  const date = new Date();
  try {
    const timeZoneIdentifier = isCode ? getTimeZoneIdentfierFromCode(timeZone) : timeZone;
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZoneIdentifier,
      hour: 'numeric',
      hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const hour = parts.find((p) => p.type === 'hour');
    return parseInt(hour.value, 10);
  } catch (e) {
    console.error(e);
    return -1;
  }
};

export const convertToPSTAndAddOneMonth = (timestamp) => {
  const [month, day, year] = convertToPST(timestamp).split('/');
  const [yronly, time] = year.split(',');
  if (month === '12') {
    return [[1, day, parseInt(yronly) + 1].join('/'), time].join(',');
  }
  return [parseInt(month) + 1, day, year].join('/');
};

export const convertToPSTDateInput = (timestamp) => {
  const [month, day, year] = convertToPST(timestamp).split('/');
  return [year, month.length > 1 ? month : '0' + month, day.length > 1 ? day : '0' + day].join('-');
};

export function formatSecondsTimestampAsDateInput(datetimestamp: number) {
  if (!isDateEmptyOrNull(datetimestamp)) {
    var newdate = new Date(datetimestamp * 1000);
    var datearr = convertDateToUTCString(newdate).split('/');
    return formatYearMonthDay(datearr[2], datearr[0], datearr[1]);
  } else {
    return null;
  }
}

function getCurrentDate() {
  const curr = new Date();
  return curr;
}

export function isApplicableForLoanType(investorLoanType: string, element: QIDData) {
  if (isStrEmptyOrNull(element.lnTyOp)) {
    return true;
  } else {
    var matchingLoanTypes = getMatchingLoanTypes(investorLoanType);
    return (
      (element.lnTyOp === '-' && !matchingLoanTypes.has(element.loanType)) ||
      (element.lnTyOp === '+' && matchingLoanTypes.has(element.loanType)) ||
      element.loanType === ''
    );
  }
}

export function isGettingDeleted(dateObj) {
  if (isObjEmptyOrNull(dateObj)) return false;
  if (!('active' in dateObj)) return false;
  if (dateObj['active']) return false;
  return true;
}

export function getErrorStr(errorSet: Array<string> | Set<string>) {
  let errorStr = '';
  errorSet.forEach((element) => {
    errorStr = errorStr + '\u2022 ' + element + '\n';
  });
  return errorStr;
}

export function isDateBeforeCurrentMonthUTC(timestamp: number) {
  const dateToCheck = epochToDate(timestamp);
  const currdate = getCurrentDate();
  const yearBefore = dateToCheck.getUTCFullYear() < currdate.getUTCFullYear();
  const monthBefore =
    dateToCheck.getUTCMonth() < currdate.getUTCMonth() && dateToCheck.getUTCFullYear() === currdate.getUTCFullYear();
  return yearBefore || monthBefore;
}

export function isDateBefore(dte1: Date, dte2: Date) {
  if (dte1.getFullYear() < dte2.getFullYear()) return true;
  if (dte1.getMonth() < dte2.getMonth() && dte1.getFullYear() === dte2.getFullYear()) return true;
  if (
    dte1.getDate() < dte2.getDate() &&
    dte1.getMonth() === dte2.getMonth() &&
    dte1.getFullYear() === dte2.getFullYear()
  )
    return true;
  return false;
}

export function isReportingDateValid(e: React.FocusEvent<HTMLInputElement>) {
  let dateinput = convertDateInputTo8AMLocal(e.target.value);
  if (dateinput.getDate() !== 1 || isDateBefore(dateinput, getReportingDateLimit())) {
    return false;
  }
  return true;
}

export function getReportingDateLimit() {
  let limitDate = new Date();
  limitDate.setMonth(limitDate.getMonth() - 1);
  limitDate.setDate(1);
  return limitDate;
}

export function areDatesOnSameDay(date1: Date, date2: Date) {
  return (
    date1.getDate() == date2.getDate() &&
    date1.getMonth() == date2.getMonth() &&
    date1.getFullYear() == date2.getFullYear()
  );
}

export function isDateInputPastOrCurrent(dateToCheck: string) {
  //yyyy-mm-dd
  const currLocal = getCurrentDate();
  const dateToCheckLocal = convertDateInputTo8AMLocal(dateToCheck);
  const yearBefore = dateToCheckLocal.getFullYear() < currLocal.getFullYear();
  const yearCurrent = dateToCheckLocal.getFullYear() === currLocal.getFullYear();
  const monthBefore = (dateToCheckLocal.getMonth() < currLocal.getMonth() && yearCurrent) || yearBefore;
  const monthCurrent = dateToCheckLocal.getMonth() === currLocal.getMonth() && yearCurrent;
  const dayBefore = monthBefore || (dateToCheckLocal.getDate() < currLocal.getDate() && monthCurrent);
  const dayCurrent = dateToCheckLocal.getDate() === currLocal.getDate() && monthCurrent;
  return dayBefore || dayCurrent;
}

export function getAbbrvMapFromQID(qid: Array<QIDData>) {
  // this maps the appreviations to the codes of the qid list
  var dict = {};
  if (!isObjEmptyOrNull(qid)) {
    for (var key in qid) {
      //
      dict[qid[key].code] = qid[key].abbreviation;
    }
  }
  return dict;
}

export function getStrValue(val) {
  if (!isStrEmptyOrNull(val)) {
    return val;
  } else {
    return '';
  }
}

export const deepEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;
  if (obj1 === null || obj2 === null) return false;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (let key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }
  return true;
};

export function formatZipCode(input: string): string {
  if (!input) return '';
  const digits = input.replace(/\D/g, '');

  if (digits.length === 9) {
    return digits.slice(0, 5) + '-' + digits.slice(5);
  }
  if (digits.length === 5) {
    return digits;
  }
  return input;
}

export function getDateValue(val: any) {
  if (typeof val === 'string') {
    if (!isStrEmptyOrNull(val)) {
      return (convertDateInputTo8AMUTC(val).getTime() / 1000).toString();
    } else {
      return '';
    }
  }
  if (!isDateEmptyOrNull(val)) {
    return formatSecondsTimestampAsDateInput(val);
  } else {
    return '';
  }
}

export function getEventDateArrayValues(arrEvents: Array<EventObj>) {
  var finalEventsArr = [];
  for (var evntKey in arrEvents) {
    var evnt = arrEvents[evntKey];
    evnt.date = getDateValue(evnt.date);
    finalEventsArr.push(evnt);
  }
  return finalEventsArr;
}

export function addMonthsUTC(startSeconds: number, months: number): number {
  const startDate = new Date(startSeconds * 1000);
  const year = startDate.getUTCFullYear();
  const month = startDate.getUTCMonth();
  const day = startDate.getUTCDate();

  const targetMonthStart = new Date(Date.UTC(year, month + months, 1));
  const daysInTargetMonth = new Date(
    Date.UTC(targetMonthStart.getUTCFullYear(), targetMonthStart.getUTCMonth() + 1, 0),
  ).getUTCDate();
  const clampedDay = Math.min(day, daysInTargetMonth);

  const targetDate = new Date(Date.UTC(targetMonthStart.getUTCFullYear(), targetMonthStart.getUTCMonth(), clampedDay));

  return Math.floor(targetDate.getTime() / 1000);
}

// Gives back the proper escrow value per each date passed in
export function getEscrowAsOfDate(isLoading: boolean, arrEscrowDates: LoanImpoundTotals, asOfDate: number) {
  let largest = -5364634029; //Control records for escrow will always start at Jan 1, 1800, which is -5364634022 in epoch time
  let escrowVal = 0;
  if (isLoading || !arrEscrowDates) return [];
  for (const escrowDate of arrEscrowDates) {
    if (escrowDate.effectPmtDte > largest && escrowDate.effectPmtDte <= asOfDate) {
      largest = escrowDate.effectPmtDte;
      escrowVal = escrowDate.mnthlyImpAmt;
    }
  }
  return escrowVal;
}

export function getNestedVal(state: any, val: string) {
  var nestedVal = null;
  const keyArray = val.split('.'); // get nested key value pair, such as dates.DDRDate
  var nestedKey = keyArray.slice(0, keyArray.length - 1).reduce((a, k) => a[k], state);
  if (nestedKey) {
    nestedVal = nestedKey[keyArray[keyArray.length - 1]];
    return nestedVal !== undefined && nestedVal !== null ? nestedVal : null;
  }
  return nestedVal;
}

export function setNestedValue(object, variable, value) {
  var path = variable.split('.');
  var last = path.pop();
  path.reduce((o, k) => (o[k] = o[k] || {}), object)[last] = value;
  return object;
}

export function handleScrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

export function getFormattedPhoneNumber(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  let res = '';
  if (digits.length < 4) {
    res = digits;
  } else if (digits.length >= 4) {
    res = `${digits.slice(0, 3)}-${digits.slice(3, 6)}`;
  }
  if (digits.length >= 7) {
    res += `-${digits.slice(6)}`;
  }
  return res;
}

export function getFormattedPhoneExt(value: string) {
  return value.replace(/\D/g, '').slice(0, 7);
}

export function addToObj(obj: object, arr: Array<object>) {
  arr = arr.filter((item) => item !== null);
  if (arr.length !== 0) {
    obj = obj === null ? {} : obj;
    arr.forEach((item) => {
      if (!isObjEmptyOrNull(item)) {
        obj = { ...obj, ...item };
      }
    });
  }
  return obj;
}

export function preventDefaultAndStopPropagation(e) {
  e?.preventDefault();
  e?.stopPropagation();
}

export function getYearUnderPlan(installments) {
  if (1 <= installments && installments <= 60) {
    return Math.floor((installments - 1) / 12) + 1;
  } else if (installments === 0) {
    return 1;
  }

  return 'No Plan Available';
}

/**
 * Get user data from master record
 *
 * @param {string} key - The key used to decrypt the storage data.
 * @returns {Promise<string>} Returns 'success' if the storage is updated successfully, or an empty string if an error occurs.
 *
 * This function sends a POST request to the server to decrypt the storage data using the provided
 * key, and then updates the local and session storage with the decrypted values. It logs any
 * errors that occur during the request.
 */
export async function getInternalUserFromMasterRecord(userId, token) {
  try {
    if (!token || !userId) return null;
    // Prepare the request to decrypt the storage data
    const request = {
      url: `live/master/user/${userId}`,
      method: 'GET',
    };
    const headers = {
      'Session-Token': token,
    };

    // Perform the request using axiosCall
    const response = await axiosCall(request, {}, headers);

    console.log(`getInternalUserFromMasterRecord.response`, response);
    return response;
  } catch (error) {
    // Log any errors that occur during the request
    console.error(`getInternalUserFromMasterRecord.caught`, error.message, error.stack);
    return null;
  }
}

export function containsCashieringTeamDutyCode(defaultDuty: string, additionalDutyCodes: any[]) {
  return (
    defaultDuty === 'CQT' ||
    additionalDutyCodes.find((dutyCode) => dutyCode.active === true && dutyCode.duty === 'CQT') !== undefined
  );
}

/**
 * Retrieves a storage key by making a request to the server.
 *
 * @returns {Promise<string>} The storage key, or an empty string if the request fails.
 *
 * This function sends a POST request to the server to generate a storage key based on the user's
 * current storage parameters. It catches any errors that occur during the request and logs them.
 */
export async function getStorageKey(authContext = null) {
  try {
    // Get the current user storage parameters
    const storage = authContext || getUserStorage();

    // Prepare the request to generate a storage key
    const request = {
      url: `/session/hash/generate`,
      method: 'POST',
      data: { parameters: storage },
    };

    // Perform the request using axiosCall
    const response = await axiosCall(request);

    // Check if the response contains a key, otherwise return an empty string
    if (!response || !response.key) return '';
    if (response.key) localStorage.setItem('key', response.key);

    return response.key;
  } catch (error) {
    // Log any errors that occur during the request
    console.error(`getStorageKey.caught`, error.message, error.stack);
    return '';
  }
}

/**
 * Updates user storage by decrypting and setting values in local and session storage.
 *
 * @param {string} key - The key used to decrypt the storage data.
 * @returns {Promise<string>} Returns 'success' if the storage is updated successfully, or an empty string if an error occurs.
 *
 * This function sends a POST request to the server to decrypt the storage data using the provided
 * key, and then updates the local and session storage with the decrypted values. It logs any
 * errors that occur during the request.
 */
export async function updateUserStorage(key: string, isServicer: boolean) {
  try {
    if (!key) return null;
    // Prepare the request to decrypt the storage data
    let response;
    if (process.env.REACT_APP_INSTANCE === 'dev' && !isServicer) {
      await fetch('https://staging.reversesoftonline.com/RestServer/session/hash/decrypt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: key,
        }),
      })
        .then((decryptResponse: Response) => {
          if (!decryptResponse.ok) {
            console.error(`updateUserStorage.caught`, decryptResponse);
          }
          return decryptResponse.json();
        })
        .then((data: any) => {
          response = data;
        });
    } else {
      const request = {
        url: `/session/hash/decrypt`,
        method: 'POST',
        data: { key: key },
      };

      // Perform the request using axiosCall
      response = await axiosCall(request);
    }

    // Check if the response contains parameters, otherwise return an empty string
    if (!response || !response.parameters) return null;

    let data = {
      ...response,
      ...response.parameters,
    };
    delete data.parameters;
    if (data.userId && data.token && !data.firstName && data.servicerUser) {
      const user = await getInternalUserFromMasterRecord(data.userId, data.token);
      if (user) {
        data = { ...data, ...user };
      }
    }

    // Update local storage with the decrypted localStorage entries
    if (data.localStorage) {
      const localStorageEntries = Object.entries(data.localStorage);
      for (const [key, value] of localStorageEntries) {
        localStorage.setItem(key, `${value}`);
      }
    }

    // Update session storage with the decrypted sessionStorage entries
    if (data.sessionStorage) {
      const sessionStorageEntries = Object.entries(data.sessionStorage);
      for (const [key, value] of sessionStorageEntries) {
        sessionStorage.setItem(key, `${value}`);
      }
    }

    localStorage.setItem('key', key);

    return data;
  } catch (error) {
    // Log any errors that occur during the request
    console.error(`updateUserStorage.caught`, error.message, error.stack);
    return null;
  } finally {
    // Remove the 'key' search parameter from the URL
    const url = new URL(window.location.href);
    url.searchParams.delete('key');
    window.history.replaceState({}, '', url.toString());
  }
}

export async function keepAlive(token) {
  try {
    if (!token) return 0;
    // Prepare the request to decrypt the storage data
    const request = {
      url: `/session/keep-alive`,
      method: 'POST',
      data: {
        expires: 1800,
      },
    };
    const headers = {
      'Session-Token': token,
    };

    // Perform the request using axiosCall
    const response = await axiosCall(request, undefined, headers);

    // Check if the response contains parameters, otherwise return an empty string
    if (!response || response.error || !response.expires) return 0;

    console.log(`keepAlive.response`, response);
    return response.expires;
  } catch (error) {
    // Log any errors that occur during the request
    console.error(`keepAlive.caught`, error.message, error.stack);
    return 0;
  }
}

export async function sessionStatus(token) {
  try {
    if (!token) return 0;
    // Prepare the request to decrypt the storage data
    const request = {
      url: `/session/status`,
      method: 'GET',
    };
    const headers = {
      'Session-Token': token,
    };

    // Perform the request using axiosCall
    const response = await axiosCall(request, undefined, headers);

    // Check if the response contains parameters, otherwise return an empty string
    if (!response || response.error || !response.expires) return 0;

    console.log(`sessionStatus.response`, response);
    return response.expires;
  } catch (error) {
    // Log any errors that occur during the request
    console.error(`sessionStatus.caught`, error.message, error.stack);
    return 0;
  }
}

export const getObjProp = (obj, prop) => {
  if (!obj || !prop) return '';
  if (Array.isArray(obj)) return '';
  if (!(prop in obj)) return '';
  if (obj[prop] === undefined) return '';
  if (obj[prop] === null) return '';
  return obj[prop];
};

// Utility to set a cookie
export const setCookie = (name, value, days) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`;
};

// Utility to get a cookie
export const getCookie = (name) => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [key, value] = cookie.trim().split('=');
    if (key === name) {
      return value;
    }
  }
  return null;
};

// Utility to delete a cookie
export const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
};

// Utility for a setTimeout() that runs regardless if tab/device is active
// When the tab is active, checks whether the timeout duration has passed
// counting from when setAbsoluteTimeout() was called
export const setAbsoluteTimeout = (callback, timeout) => {
  let startTime = Date.now();
  let callHandler = setInterval(() => {
    if (Date.now() - startTime > timeout) {
      callback();
      clearInterval(callHandler);
    }
  }, 1000);

  const clearAbsoluteTimeout = () => {
    clearInterval(callHandler);
  };

  return clearAbsoluteTimeout;
};

// Utility to format a number with commas separating thousands, millions, etc
export function formatMoney(number) {
  if (typeof number !== 'number') {
    number = parseFloat(number.replace(/[^\d.]/g, ''));
    if (isNaN(number)) return '0';
  }

  const roundedNumber = Math.round(Math.abs(number) * Math.pow(10, 2));
  const formattedNumber = roundedNumber / Math.pow(10, 2);

  const parts = formattedNumber.toFixed(2).split('.');
  if (parts.length > 1) {
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  return (number < 0 ? '-' : '') + parts.join('.');
}

export function convertToSubsidyYTDInput(YTD) {
  const tempYtd: Array<string> = [];
  if (!YTD || YTD.length === 0) return '';

  YTD.forEach((entry) => {
    if (entry.year === new Date().getFullYear()) tempYtd.push(entry.amount);
  });
  return tempYtd.join(',');
}

// transactions are processed on their process date at 1:45 PM PT and 11:45 PM PT
// 08/21/2026 - SQC-752, Justin Han-Nguyen
// Schedule has changed 2:00-2:15 PM PT and 10:15-10:30 PM PT
export function isTransactionProcessing(transactionDate) {
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  const processDate = new Date(transactionDate * 1000);

  const isToday =
    processDate.getFullYear() === now.getFullYear() &&
    processDate.getMonth() === now.getMonth() &&
    processDate.getDate() === now.getDate();

  const isBetweenFirstSchedule = now.getHours() === 14 && now.getMinutes() >= 0 && now.getMinutes() <= 15;
  const isBetweenSecondSchedule = now.getHours() === 22 && now.getMinutes() >= 15 && now.getMinutes() <= 30;

  return isToday && (isBetweenFirstSchedule || isBetweenSecondSchedule);
}

export const safeFullName = (b: any) => {
  const parts = [b?.firstName, b?.middleName, b?.lastName, b?.suffix].map((s) => (s ?? '').trim()).filter(Boolean);

  if (parts.length) return parts.join(' ');

  return (b?.fullName ?? '').trim();
};

export const safeArr = <T>(a?: (T | null | undefined)[]) => (Array.isArray(a) ? (a.filter(Boolean) as T[]) : []);

export const buildFullName = (u?: { nameFirst?: string; nameMiddle?: string | null; nameLast?: string }) =>
  [u?.nameFirst, u?.nameMiddle, u?.nameLast]
    .map((s) => (s == null ? '' : String(s).trim()))
    .filter(Boolean)
    .join(' ');

export const monthDiff = (startDate: Date, endDate: Date) =>
  Math.max(0, (endDate.getFullYear() - startDate.getFullYear()) * 12 - startDate.getMonth() + endDate.getMonth());

export const isAfter9AMPTOnJuly1st2026AndBefore9PMPTOnJuly2nd2026 = () => {
  const date = new Date();
  const month = new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    timeZone: 'America/Los_Angeles',
  }).format(date);
  const day = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    timeZone: 'America/Los_Angeles',
  }).format(date);
  const year = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    timeZone: 'America/Los_Angeles',
  }).format(date);
  const hour = new Intl.DateTimeFormat('en-US', {
    hour12: false,
    hour: '2-digit',
    timeZone: 'America/Los_Angeles',
  }).format(date);
  if (year != '2026') return false;
  if (year == '2026' && month != '07') return false;
  if (year == '2026' && month == '07' && day != '02' && day != '01') return false;
  if (year == '2026' && month == '07' && day == '01' && hour < '09') return false;
  if (year == '2026' && month == '07' && day == '02' && hour > '21') return false;
  return true;
};

export function getServStatus(loanInfo: LoanData) {
  if (!loanInfo) return '';
  if (!loanInfo.servStatus) return '';
  var desc = loanInfo.servStatusDescription;
  if (loanInfo.servStatus === '0') {
    let nextPmt = new Date(addMonthsYrsToTimestamp(loanInfo.lpi, 1, 0) * 1000);
    let nDays = daysBetween(nextPmt, new Date());
    if (nDays > 1) desc = nDays + ' days late';
    else if (nDays === 1) desc = nDays + ' day late';
    else desc = 'Current';
  }
  return desc;
}

/**
 * SQC-689 - Daniel Rivas
 *
 * @param {string | number | Date | null | underfined } value - The date value to validate.
 * @param { number } minYear - The minimum allowed year. This comes from swlimits.h.
 * @param { number } maxYear - The maximum allowed year. This comes from swlimits.h.
 * @returns {DateValidationResult} Returns whether the date is valid and an error message if invalid.
 *
 * If SW_MIN_YEAR or SWM_MAX_YEAR change, this validation should be update to match u2.
 */
export type DateValidationResult = {
  isValid: boolean;
  message: string;
};

export const validateDateYear = (
  value: string | number | Date | null | undefined,
  minYear = 1800,
  maxYear = 2200,
): DateValidationResult => {
  if (value === null || value === undefined || value === '') {
    return {
      isValid: true,
      message: '',
    };
  }

  let year: number | null = null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return {
        isValid: false,
        message: 'Invalid Date',
      };
    }

    year = value.getFullYear();
  } else if (typeof value === 'number') {
    const date = new Date(value * 1000);

    if (Number.isNaN(date.getTime())) {
      return {
        isValid: false,
        message: 'Invalid Date',
      };
    }

    year = date.getFullYear();
  } else {
    const trimmedValue = value.trim();

    const yyyyMmDdMatch = /^(\d{4})-\d{2}-d{2}$/.exec(trimmedValue);
    const mmDdYyyyMatch = /^\d{1,2}\/\d{1,2}\/(\d{4})$/.exec(trimmedValue);

    if (yyyyMmDdMatch) {
      year = Number(yyyyMmDdMatch[1]);
    } else if (mmDdYyyyMatch) {
      year = Number(mmDdYyyyMatch[1]);
    } else {
      return {
        isValid: false,
        message: 'Invalid Date',
      };
    }
  }

  if (year < minYear || year > maxYear) {
    return {
      isValid: false,
      message: `Date year must be between ${minYear} and ${maxYear}`,
    };
  }

  return {
    isValid: true,
    message: '',
  };
};

export const formatEIN = (value: string = '') => {
  const digits = value.replace(/\D/g, '').slice(0, 9);

  return digits.replace(/^(\d{3})(\d)/, '$1-$2').replace(/^(\d{3})-(\d{3})(\d)/, '$1-$2-$3');
};

export const getDateFromValue = (value: number | string | null | undefined): Date | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  let date: Date;

  if (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value))) {
    const numericValue = Number(value);
    const milliseconds = numericValue < 1000000000000 ? numericValue * 1000 : numericValue;
    date = new Date(milliseconds);
  } else {
    date = new Date(value);
  }

  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateValue = (value: number | string | null | undefined): string => {
  const date = getDateFromValue(value);

  if (!date) {
    return '';
  }

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

export const formatDateValueForInput = (value: number | string | null | undefined): string => {
  const date = getDateFromValue(value);

  if (!date) {
    return '';
  }

  return formatDateYYYYMMDD(date);
};

export const dateInputToUnix = (value: string): number | null => {
  if (!value) {
    return null;
  }

  const date = new Date(`${value}T00:00:00`);

  return Math.floor(date.getTime() / 1000);
};

export const dateToUnix = (date: Date): number => {
  return Math.floor(new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() / 1000);
};

export const addDaysToDateValue = (value: number | string | null | undefined, numberOfDays: number): number | null => {
  const date = getDateFromValue(value);

  if (!date) {
    return null;
  }

  const newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  newDate.setDate(newDate.getDate() + numberOfDays);

  return dateToUnix(newDate);
};

export const addBusinessDay = (value: number | string | null | undefined): number | null => {
  const date = getDateFromValue(value);

  if (!date) {
    return null;
  }

  const newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  do {
    newDate.setDate(newDate.getDate() + 1);
  } while (newDate.getDate() === 0 || newDate.getDay() === 6);

  return dateToUnix(newDate);
};

export const getDaysBetween = (firstValue: number | string | null | undefined, secondDate: Date): number | null => {
  const firstDate = getDateFromValue(firstValue);

  if (!firstDate) {
    return null;
  }

  const first = new Date(firstDate.getFullYear(), firstDate.getMonth(), firstDate.getDate());
  const second = new Date(secondDate.getFullYear(), secondDate.getMonth(), secondDate.getDate());
  const difference = second.getTime() - first.getTime();

  return Math.floor(difference / (1000 * 60 * 60 * 24));
};
